import { useIdleTimerContext } from "@src/context/IdleTimerContext";
import { useEffect, useRef } from "react";

/**
 * Registers a draft-flush with IdleTimerContext so that when the user clicks
 * "I'm here" on the idle warning modal, the currently active form's in-progress
 * local state is saved to ApplicationContext (which auto-persists to localStorage)
 * without triggering validation.
 *
 * Usage — call once inside a form component after the saveToContext callback
 * is declared:
 *
 *   useIdleFormFlush(saveToContext);
 *
 * Pass `{ flushOnUnmount: true }` to also flush when the component unmounts
 * (e.g. back-navigation), which preserves partial form state across route changes:
 *
 *   useIdleFormFlush(saveToContext, { flushOnUnmount: true });
 *
 * @param saveToContext - A stable or unstable callback that writes the form's
 *   current local state to ApplicationContext via setData / setDataPatch.
 *   The hook holds it in a ref so it always reads the latest form state without
 *   requiring re-registration on every render.
 * @param options.flushOnUnmount - When true, also flushes on component unmount.
 */
export const useIdleFormFlush = (
  saveToContext: () => void,
  options?: { flushOnUnmount?: boolean },
): void => {
  const { registerFlushCallback, unregisterFlushCallback, checkIsSessionEnding } =
    useIdleTimerContext();
  const flushOnUnmount = options?.flushOnUnmount ?? false;

  // Keep a ref to the latest saveToContext so the registered callback is always
  // current without needing to re-register on every render.
  const latestRef = useRef(saveToContext);
  // Update during render so the flush callback never sees a stale function between render and effect.
  latestRef.current = saveToContext;

  // Register once on mount, unregister on unmount.
  useEffect(() => {
    registerFlushCallback(() => {
      latestRef.current();
    });
    return () => {
      unregisterFlushCallback();
      if (flushOnUnmount && !checkIsSessionEnding()) {
        latestRef.current();
      }
    };
  }, [registerFlushCallback, unregisterFlushCallback, flushOnUnmount, checkIsSessionEnding]);
};
